import { ethers } from 'ethers';
import { erc20Abi } from '../abis';
import { swmAddress, poolAddress, permitDeadlineSeconds } from '../constants';
import { RPCErrorHandler } from '../utils/rpcErrorHandling';
import { retryWrapper, waitForTx } from '../utils/txReliability';

export interface PermitSignature {
  v: number;
  r: string;
  s: string;
  deadline: number;
}

export interface SWMServiceResult {
  success: boolean;
  transactionHash?: string;
  error?: string;
}

export class SWMService {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.Signer | null = null;
  private contract: ethers.Contract | null = null;
  private lastNetworkValidation: number = 0;
  private networkValidationCacheMs: number = 5000; // Cache for 5 seconds

  private async initializeProvider() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);
      // Initialize read-only contract; acquire signer lazily on user actions
      this.contract = new ethers.Contract(swmAddress, erc20Abi, this.provider);

      // Validate network connection
      await this.validateNetwork();
    }
  }

  /**
   * Validate that we're connected to the correct network
   */
  private async validateNetwork(): Promise<void> {
    if (!this.provider) return;

    // Check if we've validated recently (within cache time)
    const now = Date.now();
    if (now - this.lastNetworkValidation < this.networkValidationCacheMs) {
      return; // Skip validation if recently validated
    }

    try {
      let network;
      try {
        network = await this.provider.getNetwork();
      } catch (e: any) {
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('network changed')) {
          // transient during MetaMask chain switch
          throw new Error('TRANSIENT_NETWORK_CHANGE');
        }
        throw e;
      }
      const expectedChainId = BigInt('0x142F55'); // Swarm devnet: 1322837

      if (network.chainId !== expectedChainId) {
        // Signal wrong network without spamming console
        const err = new Error('WRONG_NETWORK');
        (err as any).expected = expectedChainId.toString();
        (err as any).actual = network.chainId.toString();
        throw err;
      }

      // Update cache timestamp
      this.lastNetworkValidation = now;
    } catch (error) {
      // Rethrow for caller handling; callers will treat TRANSIENT or WRONG_NETWORK as soft failures
      throw error;
    }
  }

  private async ensureInitialized() {
    if (!this.provider || !this.contract) {
      await this.initializeProvider();
    }

    if (!this.provider || !this.contract) {
      throw new Error('Provider not initialized');
    }
  }

  private async ensureSigner() {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }
    if (!this.signer) {
      this.signer = await this.provider.getSigner();
    }
  }

  /**
   * Get contract instance for write operations (using signer)
   */
  private getWriteContract(): ethers.Contract {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }
    return new ethers.Contract(swmAddress, erc20Abi, this.signer);
  }

  /**
   * Get SWM token balance for an address with proper 9-decimal formatting
   */
  async getBalance(address: string): Promise<string> {
    try {
      await this.ensureInitialized();

      // Validate network connection first
      try {
        await this.validateNetwork();
      } catch (networkError: any) {
        const msg = String(networkError?.message || '').toUpperCase();
        if (msg.includes('TRANSIENT_NETWORK_CHANGE') || msg.includes('WRONG_NETWORK')) {
          return '0';
        }
        return '0';
      }

      // Use RPC error handling with retry logic
      const result = await RPCErrorHandler.executeWithRetry(
        () => this.contract!.balanceOf(address),
        { maxRetries: 5, retryDelay: 500, backoffMultiplier: 1.5 }
      );

      if (!result.success) {
        console.error('SWM balance call failed after retries:', result.error);
        return '0';
      }

      const balance = result.data;
      return ethers.formatUnits(balance, 9);
    } catch (error) {
      // Log RPC error with enhanced context
      if (error instanceof Error) {
        RPCErrorHandler.logRPCError('getSWMBalance', error, {
          address,
        });
      }
      return '0';
    }
  }

  /**
   * Get SWM token allowance for the pool contract with proper 9-decimal formatting
   */
  async getAllowance(
    owner: string,
    spender: string = poolAddress
  ): Promise<string> {
    try {
      await this.ensureInitialized();

      // Validate network connection first
      try {
        await this.validateNetwork();
      } catch (networkError: any) {
        const msg = String(networkError?.message || '').toUpperCase();
        if (msg.includes('TRANSIENT_NETWORK_CHANGE') || msg.includes('WRONG_NETWORK')) {
          return '0';
        }
        return '0';
      }

      // Use RPC error handling with retry logic
      const result = await RPCErrorHandler.executeWithRetry(
        () => this.contract!.allowance(owner, spender),
        { maxRetries: 5, retryDelay: 500, backoffMultiplier: 1.5 }
      );

      if (!result.success) {
        console.error('SWM allowance call failed after retries:', result.error);
        return '0';
      }

      const allowance = result.data;
      return ethers.formatUnits(allowance, 9);
    } catch (error) {
      // Log RPC error with enhanced context
      if (error instanceof Error) {
        RPCErrorHandler.logRPCError('getSWMAllowance', error, {
          owner,
          spender,
        });
      }
      return '0';
    }
  }

  /**
   * Parse SWM amount string to proper 9-decimal wei format
   */
  parseAmount(amount: string): bigint {
    return ethers.parseUnits(amount, 9);
  }

  /**
   * Format SWM wei amount to human-readable string with 9 decimals
   */
  formatAmount(amount: bigint): string {
    return ethers.formatUnits(amount, 9);
  }

  /**
   * Generate EIP-2612 permit signature using MetaMask's eth_signTypedData_v4
   */
  async generatePermitSignature(
    owner: string,
    spender: string,
    value: string,
    deadline?: number
  ): Promise<PermitSignature> {
    try {
      await this.ensureInitialized();
      await this.ensureSigner();

      if (!deadline) {
        deadline = Math.floor(Date.now() / 1000) + permitDeadlineSeconds;
      }

      const nonce = await this.contract!.nonces(owner);
      const valueWei = this.parseAmount(value);

      // Get domain separator from contract
      const domainSeparator = await this.contract!.DOMAIN_SEPARATOR();

      // EIP-712 domain
      const domain = {
        name: 'Swarm Token',
        version: '1',
        chainId: await this.provider!.getNetwork().then(n => n.chainId),
        verifyingContract: swmAddress,
      };

      // EIP-712 types
      const types = {
        Permit: [
          { name: 'owner', type: 'address' },
          { name: 'spender', type: 'address' },
          { name: 'value', type: 'uint256' },
          { name: 'nonce', type: 'uint256' },
          { name: 'deadline', type: 'uint256' },
        ],
      };

      // Message to sign
      const message = {
        owner,
        spender,
        value: valueWei.toString(),
        nonce: nonce.toString(),
        deadline: deadline.toString(),
      };

      // Sign using MetaMask's eth_signTypedData_v4
      // Sign with transient-aware retry (MetaMask sometimes fails due to RPC lookups)
      const signature = await retryWrapper(
        () => this.signer!.signTypedData(domain, types, message),
        4,
        200
      );

      // Split signature into v, r, s components
      const sig = ethers.Signature.from(signature);

      return {
        v: sig.v,
        r: sig.r,
        s: sig.s,
        deadline,
      };
    } catch (error) {
      console.error('Error generating permit signature:', error);
      throw new Error(
        `Failed to generate permit signature: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Execute permit approval using generated signature
   */
  async executePermit(
    owner: string,
    spender: string,
    value: string,
    signature: PermitSignature
  ): Promise<SWMServiceResult> {
    try {
      await this.ensureInitialized();
      await this.ensureSigner();

      const valueWei = this.parseAmount(value);

      const writeContract = this.getWriteContract();
      const tx = await retryWrapper(
        () =>
          writeContract.permit(
            owner,
            spender,
            valueWei,
            signature.deadline,
            signature.v,
            signature.r,
            signature.s
          ),
        3,
        400
      );

      // robust wait
      await waitForTx(this.provider!, tx.hash, 1, 120000);

      return {
        success: true,
        transactionHash: tx.hash,
      };
    } catch (error) {
      console.error('Error executing permit:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Standard approve method as fallback
   */
  async approve(spender: string, amount: string): Promise<SWMServiceResult> {
    try {
      await this.ensureInitialized();
      await this.ensureSigner();

      const amountWei = this.parseAmount(amount);
      const writeContract = this.getWriteContract();
      const tx = await retryWrapper(
        () => writeContract.approve(spender, amountWei),
        3,
        400
      );
      await waitForTx(this.provider!, tx.hash, 1, 120000);

      return {
        success: true,
        transactionHash: tx.hash,
      };
    } catch (error) {
      console.error('Error approving SWM tokens:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Check if user has sufficient balance for the specified amount
   */
  async checkBalance(userAddress: string, amount: string): Promise<boolean> {
    try {
      const balance = await this.getBalance(userAddress);
      const balanceWei = this.parseAmount(balance);
      const amountWei = this.parseAmount(amount);

      return balanceWei >= amountWei;
    } catch (error) {
      console.error('Error checking SWM balance:', error);
      return false;
    }
  }

  /**
   * Check if user has sufficient allowance for the specified amount
   */
  async checkAllowance(
    userAddress: string,
    amount: string,
    spender: string = poolAddress
  ): Promise<boolean> {
    try {
      const allowance = await this.getAllowance(userAddress, spender);
      const allowanceWei = this.parseAmount(allowance);
      const amountWei = this.parseAmount(amount);

      return allowanceWei >= amountWei;
    } catch (error) {
      console.error('Error checking SWM allowance:', error);
      return false;
    }
  }

  /**
   * Complete approval flow with permit-first approach
   */
  async approveWithPermitFallback(
    owner: string,
    spender: string,
    amount: string
  ): Promise<SWMServiceResult> {
    try {
      // First try permit signature
      const signature = await this.generatePermitSignature(
        owner,
        spender,
        amount
      );
      const permitResult = await this.executePermit(
        owner,
        spender,
        amount,
        signature
      );

      if (permitResult.success) {
        return permitResult;
      }

      // Fallback to standard approve
      console.log('Permit failed, falling back to standard approve');
      return await this.approve(spender, amount);
    } catch (error) {
      // If permit signature fails (user rejection, etc.), fallback to approve
      console.log('Permit signature failed, falling back to standard approve');
      return await this.approve(spender, amount);
    }
  }
}

// Export singleton instance
export const swmService = new SWMService();
